import { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  useTours,
  useStartTour,
  useCompleteStep,
  useCompleteTour,
  useSkipTour,
  useRemindLater,
  useExitTour,
  useResumeTour,
  useHouseholds,
  shouldPromptTour,
  getTourById,
  type TourWithProgress,
  type DeviceType,
  type SkipReason,
} from '@pantry/api-client';
import type { TourPath, TourStep } from '@pantry/shared';
import { translateStepField } from '@pantry/i18n';
import { usePostHog } from 'posthog-js/react';
import {
  getPathFilteredSteps,
  resolveTourPath,
  mapFullIndexToFilteredIndex,
} from './tour-context.helpers';
import { useTourTriggers } from './tour-triggers';
import {
  resolveNavigateTo,
  resolveStartRoute,
  POPOVER_RENDER_DELAY,
  PATH_SWITCH_DELAY,
  NAVIGATION_SETTLE_DELAY,
  HANDOFF_DELAY,
  HIGHLIGHT_SETTLE_DELAY,
} from './tour-navigation';
import {
  convertToDriverSteps,
  getFilteredSteps,
  driveTourStepWhenReady,
  createPauseButton,
  createPathChoiceElement,
} from './tour-driver';

interface TourContextValue {
  /** Whether a tour is currently active */
  isActive: boolean;
  /** Current tour ID if active */
  currentTourId: string | null;
  /** Current step ID if a tour is active */
  currentStepId: string | null;
  /** All tours with progress */
  tours: TourWithProgress[] | undefined;
  /** Whether tours are loading */
  isLoading: boolean;
  /** Start a specific tour */
  startTour: (tourId: string) => Promise<void>;
  /** Complete the current step */
  completeCurrentStep: () => Promise<void>;
  /** Complete the entire tour */
  completeTour: () => Promise<void>;
  /** Skip the current tour */
  skipTour: (reason?: SkipReason) => Promise<void>;
  /** Remind later for the current tour */
  remindLater: () => Promise<void>;
  /** Exit the current tour (save progress) */
  exitTour: () => Promise<void>;
  /** Resume a tour from saved progress */
  resumeTour: (tourId: string) => Promise<void>;
  /** Check if a tour should be prompted */
  shouldPrompt: (tourId: string) => boolean;
  /** Dismiss the tour prompt without taking action */
  dismissPrompt: () => void;
  /** Tour ID to prompt (if any) */
  tourToPrompt: string | null;
  /** Pause the current tour (for interactive steps) */
  pauseTour: () => Promise<void>;
  /** Set the first household ID for navigation purposes */
  setFirstHouseholdId: (id: string | null) => void;
  /** Get the first household ID */
  firstHouseholdId: string | null;
  /** Set the current item count (used for trigger guards) */
  setItemCount: (count: number) => void;
  /** Current tour path (create or join) */
  currentTourPath: TourPath | null;
  /** Select a tour path (triggers path-specific steps) */
  selectTourPath: (path: TourPath) => void;
  /** Whether tour is waiting for user to get a real household */
  isWaitingForHousehold: boolean;
}

export const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
  children: React.ReactNode;
}

const DEVICE_TYPE: DeviceType = 'web';

export function TourProvider({ children }: TourProviderProps) {
  const { i18n } = useLingui();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const driverRef = useRef<Driver | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [firstHouseholdId, setFirstHouseholdId] = useState<string | null>(null);
  const [itemCount, setItemCount] = useState(0);
  const pendingNavigationRef = useRef<{ tourId: string; stepIndex: number } | null>(null);
  const pendingPathRef = useRef<TourPath | null>(null);
  const handoffInProgressRef = useRef(false);

  // Path state
  const [currentTourPath, setCurrentTourPath] = useState<TourPath | null>(null);
  const [isWaitingForHousehold, setIsWaitingForHousehold] = useState(false);

  // Get real households to check if user has any
  const { data: households, isLoading: householdsLoading } = useHouseholds();

  // API hooks
  const { data: toursData, isLoading } = useTours();
  const startTourMutation = useStartTour();
  const completeStepMutation = useCompleteStep();
  const completeTourMutation = useCompleteTour();
  const skipTourMutation = useSkipTour();
  const remindLaterMutation = useRemindLater();
  const exitTourMutation = useExitTour();
  const resumeTourMutation = useResumeTour();

  const tours = toursData?.tours;

  // Tour trigger logic (immediate + on_route prompting)
  const { tourToPrompt, setTourToPrompt, dismissPrompt } = useTourTriggers({
    tours,
    isLoading,
    isActive,
    itemCount,
    posthog,
  });

  // Handle tour completion - defined as refs to avoid circular dependencies
  const handleTourCompleteRef = useRef<
    ((tourId: string, handoffTo?: string) => Promise<void>) | undefined
  >(undefined);
  const handleTourExitRef = useRef<((tourId: string) => Promise<void>) | undefined>(undefined);
  const startTourRef = useRef<((tourId: string) => Promise<void>) | undefined>(undefined);

  // Forward declaration for initializeDriver (used in navigation handling)
  const initializeDriverRef = useRef<
    ((tourId: string, startIndex?: number, path?: TourPath | null) => Driver | null) | undefined
  >(undefined);

  // Update ref callbacks when dependencies change
  useEffect(() => {
    handleTourCompleteRef.current = async (tourId: string, handoffTo?: string) => {
      try {
        await completeTourMutation.mutateAsync({
          tourId,
          deviceType: DEVICE_TYPE,
        });
        posthog?.capture('tour_completed', {
          tour_id: tourId,
          device_type: DEVICE_TYPE,
          steps_completed: currentStepIndex + 1,
        });
      } catch (error) {
        console.error('Failed to complete tour:', error);
      }
      driverRef.current = null;

      // Handle handoff to next tour
      if (handoffTo) {
        // Keep isActive true during handoff to prevent useTourTriggers from
        // prompting another tour during the HANDOFF_DELAY window
        setCurrentTourId(null);
        setCurrentStepId(null);

        posthog?.capture('tour_handoff', {
          from_tour: tourId,
          to_tour: handoffTo,
          device_type: DEVICE_TYPE,
        });
        setTimeout(() => {
          handoffInProgressRef.current = false;
          startTourRef.current?.(handoffTo);
        }, HANDOFF_DELAY);
      } else {
        // No handoff — fully deactivate
        handoffInProgressRef.current = false;
        setIsActive(false);
        setCurrentTourId(null);
        setCurrentStepId(null);
      }
    };

    handleTourExitRef.current = async (tourId: string) => {
      try {
        await exitTourMutation.mutateAsync({
          tourId,
          deviceType: DEVICE_TYPE,
        });
        posthog?.capture('tour_exited', {
          tour_id: tourId,
          device_type: DEVICE_TYPE,
          step_index: currentStepIndex,
        });
      } catch (error) {
        console.error('Failed to exit tour:', error);
      }
      driverRef.current?.destroy();
      setIsActive(false);
      setCurrentTourId(null);
      setCurrentStepId(null);
      driverRef.current = null;
    };
  }, [completeTourMutation, exitTourMutation, posthog, currentStepIndex, households]);

  // Initialize Driver.js instance with support for interactive steps and path filtering
  const initializeDriver = useCallback(
    (tourId: string, _startIndex = 0, path: TourPath | null = null) => {
      const steps = convertToDriverSteps(i18n, tourId, path);
      if (steps.length === 0) return null;

      const tourSteps = getFilteredSteps(tourId, path);

      const driverInstance = driver({
        steps,
        animate: true,
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        allowClose: true,
        allowKeyboardControl: true,
        overlayColor: 'rgba(0, 0, 0, 0.75)',
        stagePadding: 10,
        stageRadius: 8,
        popoverClass: 'pantry-tour-popover',
        popoverOffset: 12,
        progressText: `{{current}} ${i18n._(msg({ message: `of`, context: `tour: step progress counter` }))} {{total}}`,
        nextBtnText: i18n._(msg({ message: `Next`, context: `tour: navigation button` })),
        prevBtnText: i18n._(msg({ message: `Back`, context: `tour: navigation button` })),
        doneBtnText: i18n._(msg({ message: `Done`, context: `tour: navigation button` })),
        onHighlighted: (_element, _step, { state }) => {
          const stepIndex = state.activeIndex ?? 0;
          setCurrentStepIndex(stepIndex);

          const currentStep = tourSteps[stepIndex];
          setCurrentStepId(currentStep?.id ?? null);

          // Change "Done" button to "Continue" when this step hands off to another tour
          if (currentStep?.handoffTo) {
            setTimeout(() => {
              const doneBtn = document.querySelector('.driver-popover-done-btn') as HTMLElement;
              if (doneBtn) {
                doneBtn.textContent = i18n._(
                  msg({ message: `Continue`, context: `tour: handoff to next tour` })
                );
              }
            }, HIGHLIGHT_SETTLE_DELAY);
          }

          // Add custom pause button
          if (currentStep?.canPause) {
            setTimeout(() => {
              const popoverFooter = document.querySelector('.driver-popover-footer');
              if (popoverFooter && !document.querySelector('.tour-pause-btn')) {
                const label = currentStep.pauseLabel
                  ? translateStepField(
                      i18n,
                      tourId,
                      currentStep.id,
                      'pauseLabel',
                      currentStep.pauseLabel
                    )
                  : i18n._(msg({ message: `Continue later`, context: `tour: pause button` }));
                const pauseBtn = createPauseButton({
                  label,
                  onPause: () => handleTourExitRef.current?.(tourId),
                });
                popoverFooter.appendChild(pauseBtn);
              }
            }, POPOVER_RENDER_DELAY);
          }

          // Path choice rendering
          if (currentStep?.showPathChoice) {
            const persistPathSelection = async (selectedPath: TourPath) => {
              if (!currentStep?.id) return;
              try {
                await completeStepMutation.mutateAsync({
                  tourId,
                  deviceType: DEVICE_TYPE,
                  stepId: currentStep.id,
                  selectedPath,
                });
              } catch (error) {
                console.error('Failed to persist tour path selection:', error);
              }
            };

            // If user already has a household, replace the path choice with a
            // tailored message and a Continue button that hands off to inventory-entry
            const hasRealHousehold = !householdsLoading && households && households.length > 0;

            setTimeout(() => {
              if (hasRealHousehold) {
                // Replace the entire popover for a clean "has household" experience
                const titleEl = document.querySelector('.driver-popover-title');
                const descEl = document.querySelector('.driver-popover-description');
                const progressEl = document.querySelector('.driver-popover-progress-text');
                const popoverFooter = document.querySelector('.driver-popover-footer');

                if (titleEl) {
                  titleEl.textContent = i18n._(
                    msg({
                      message: `Household Ready`,
                      context: `tour: households tour, user already has household`,
                    })
                  );
                }
                if (descEl) {
                  descEl.textContent = i18n._(
                    msg({
                      message: `You already have a household set up. You can manage its settings anytime from here. Let's head to your inventory and add your first item.`,
                      context: `tour: households tour, user already has household`,
                    })
                  );
                }
                // Hide progress counter — this is a simplified flow
                if (progressEl) {
                  (progressEl as HTMLElement).style.display = 'none';
                }
                // Replace footer with a single Continue button
                if (popoverFooter) {
                  while (popoverFooter.firstChild) {
                    popoverFooter.removeChild(popoverFooter.firstChild);
                  }
                  const continueBtn = document.createElement('button');
                  continueBtn.textContent = i18n._(
                    msg({ message: `Continue`, context: `tour: handoff to next tour` })
                  );
                  continueBtn.className = 'driver-popover-next-btn';
                  continueBtn.addEventListener('click', () => {
                    handoffInProgressRef.current = true;
                    driverRef.current?.destroy();
                    posthog?.capture('tour_auto_skipped', {
                      tour_id: tourId,
                      reason: 'user_has_household',
                      device_type: DEVICE_TYPE,
                    });
                    handleTourCompleteRef.current?.(tourId, 'inventory-entry');
                  });
                  popoverFooter.appendChild(continueBtn);
                }
                return;
              }

              const popoverFooter = document.querySelector('.driver-popover-footer');
              if (popoverFooter && !document.querySelector('.tour-path-choice')) {
                // Hide ALL default footer elements
                const footerChildren = popoverFooter.querySelectorAll(':scope > *');
                footerChildren.forEach((child) => {
                  (child as HTMLElement).style.display = 'none';
                });

                const handlePathSelect = async (selectedPath: TourPath) => {
                  setCurrentTourPath(selectedPath);
                  await persistPathSelection(selectedPath);
                  driverRef.current?.destroy();
                  posthog?.capture('tour_path_selected', {
                    tour_id: tourId,
                    path: selectedPath,
                  });
                  setTimeout(() => {
                    const newDriver = initializeDriverRef.current?.(tourId, 0, selectedPath);
                    if (newDriver) {
                      driverRef.current = newDriver;
                      const filteredSteps = getFilteredSteps(tourId, selectedPath);
                      const firstPathStepIndex = filteredSteps.findIndex(
                        (s) => s.path === selectedPath
                      );
                      newDriver.drive(firstPathStepIndex >= 0 ? firstPathStepIndex : 0);
                    }
                  }, PATH_SWITCH_DELAY);
                };

                const choiceElement = createPathChoiceElement({
                  createLabel: i18n._(
                    msg({
                      message: `Create my first household`,
                      context: `tour: household path choice button`,
                    })
                  ),
                  joinLabel: i18n._(
                    msg({
                      message: `I'm waiting for an invite`,
                      context: `tour: household path choice button`,
                    })
                  ),
                  onCreatePath: () => handlePathSelect('create'),
                  onJoinPath: () => handlePathSelect('join'),
                });
                popoverFooter.appendChild(choiceElement);
              }
            }, POPOVER_RENDER_DELAY);
          }
        },
        onNextClick: async (_element, _step, { state, driver: d }) => {
          const stepIndex = state.activeIndex ?? 0;
          const currentStep = tourSteps[stepIndex];
          const stepId = currentStep?.id;

          // Complete the current step
          if (stepId) {
            try {
              await completeStepMutation.mutateAsync({
                tourId,
                deviceType: DEVICE_TYPE,
                stepId,
                selectedPath: path ?? undefined,
              });
              posthog?.capture('tour_step_completed', {
                tour_id: tourId,
                device_type: DEVICE_TYPE,
                step_id: stepId,
                step_index: stepIndex,
              });
            } catch (error) {
              console.error('Failed to complete step:', error);
            }
          }

          // User has a household and is on the path choice step — complete and hand off
          if (
            currentStep?.showPathChoice &&
            !householdsLoading &&
            households &&
            households.length > 0
          ) {
            handoffInProgressRef.current = true;
            d.destroy();
            posthog?.capture('tour_auto_skipped', {
              tour_id: tourId,
              reason: 'user_has_household',
              device_type: DEVICE_TYPE,
            });
            handleTourCompleteRef.current?.(tourId, 'inventory-entry');
            return;
          }

          // Handle checkHouseholdAndPause - end of Path 2
          if (currentStep?.checkHouseholdAndPause) {
            d.destroy();
            const hasRealHousehold = !householdsLoading && households && households.length > 0;
            if (hasRealHousehold) {
              handleTourCompleteRef.current?.(tourId, currentStep?.handoffTo || 'inventory-entry');
            } else {
              setIsWaitingForHousehold(true);
              setIsActive(false);
              setCurrentTourId(null);
              setCurrentStepId(null);
              posthog?.capture('tour_waiting_for_household', {
                tour_id: tourId,
                device_type: DEVICE_TYPE,
              });
              navigate({ to: '/households' });
            }
            return;
          }

          // Handle navigation if this step has navigateTo
          if (currentStep?.navigateTo) {
            const route = resolveNavigateTo(currentStep.navigateTo, firstHouseholdId);
            if (route && location.pathname !== route) {
              d.destroy();
              pendingNavigationRef.current = { tourId, stepIndex: stepIndex + 1 };
              pendingPathRef.current = path;
              navigate({ to: route });
              return;
            }
          }

          // Interactive steps require the user to complete an action before advancing.
          // When the waitForElement is not yet in the DOM, we auto-click the step's
          // target to nudge the user (e.g., open a dialog). If the element already
          // exists, the user has completed the action and we advance normally.
          if (currentStep?.isInteractive && currentStep?.waitForElement) {
            const requiredElement = document.querySelector(currentStep.waitForElement);
            if (!requiredElement) {
              if (currentStep.action === 'add_item' || currentStep.action === 'create_household') {
                // Auto-click the highlighted target to open the relevant dialog/form.
                // For 'add_item' this opens the add-item sheet; for 'create_household'
                // this opens the create-household dialog. Steps where auto-click is
                // inappropriate (e.g. name-household where the user must type) simply
                // return without action — the waitForElement won't exist until they submit.
                const triggerElement = document.querySelector(currentStep.target);
                if (triggerElement instanceof HTMLElement) {
                  triggerElement.click();
                  // Poll for the waitForElement to appear after the auto-click
                  // (e.g., form dialog opening asynchronously), then auto-advance
                  const selector = currentStep.waitForElement!;
                  let pollCount = 0;
                  const pollInterval = setInterval(() => {
                    pollCount++;
                    if (document.querySelector(selector)) {
                      clearInterval(pollInterval);
                      d.moveNext();
                    } else if (pollCount > 30) {
                      // Stop polling after ~3 seconds
                      clearInterval(pollInterval);
                    }
                  }, 100);
                }
              }
              return;
            }
            d.moveNext();
            return;
          }

          // Check if this is the last step
          if (d.isLastStep()) {
            // Signal handoff before destroy so onDestroyed won't clear isActive
            if (currentStep?.handoffTo) {
              handoffInProgressRef.current = true;
            }
            d.destroy();
            handleTourCompleteRef.current?.(tourId, currentStep?.handoffTo);
          } else {
            d.moveNext();
          }
        },
        onCloseClick: () => {
          handleTourExitRef.current?.(tourId);
        },
        onDestroyed: () => {
          if (!pendingNavigationRef.current && !handoffInProgressRef.current) {
            setIsActive(false);
            setCurrentTourId(null);
            setCurrentStepId(null);
          }
        },
      });

      return driverInstance;
    },
    [
      i18n,
      completeStepMutation,
      posthog,
      firstHouseholdId,
      households,
      householdsLoading,
      location.pathname,
      navigate,
    ]
  );

  // Store initializeDriver in ref for use in callbacks
  useEffect(() => {
    initializeDriverRef.current = initializeDriver;
  }, [initializeDriver]);

  // Handle resuming tour after navigation.
  // Two-layer readiness strategy:
  //   1. NAVIGATION_SETTLE_DELAY (500ms) lets the React route component mount.
  //   2. driveTourStepWhenReady then polls for the target element (up to 1000ms).
  // Combined budget of ~1500ms handles slow renders without a fixed long delay.
  useEffect(() => {
    if (pendingNavigationRef.current) {
      const { tourId, stepIndex } = pendingNavigationRef.current;
      const path = pendingPathRef.current;
      pendingNavigationRef.current = null;

      pendingPathRef.current = null;

      setTimeout(() => {
        const driverInstance = initializeDriver(tourId, stepIndex, path);
        if (driverInstance) {
          driverRef.current = driverInstance;
          void driveTourStepWhenReady(
            driverInstance,
            () => driverRef.current === driverInstance,
            tourId,
            stepIndex,
            path
          );
        }
      }, NAVIGATION_SETTLE_DELAY);
    }
  }, [location.pathname, initializeDriver]);

  // Auto-resume tour when user gets a household (after Path 2 waiting state).
  // This effect is route-agnostic: it fires whenever the households query updates,
  // regardless of which page the user is on. startTour handles its own navigation.
  // Note: isWaitingForHousehold lives in React state, so a page refresh resets it.
  // That's acceptable — tour progress is persisted server-side via exitTourMutation.
  useEffect(() => {
    if (isWaitingForHousehold && households && households.length > 0) {
      setIsWaitingForHousehold(false);
      posthog?.capture('tour_resumed_after_household', {
        device_type: DEVICE_TYPE,
      });
      setTimeout(() => {
        startTourRef.current?.('inventory-entry');
      }, HANDOFF_DELAY);
    }
  }, [isWaitingForHousehold, households, posthog]);

  // Clean up active driver instance
  const cleanupDriver = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
    setCurrentStepId(null);
    pendingNavigationRef.current = null;

    pendingPathRef.current = null;
  }, []);

  /**
   * Shared logic for starting or resuming a tour after the mutation returns.
   * Computes step indices, captures analytics, sets state, resolves route, and initializes the driver.
   */
  const activateAndDriveTour = useCallback(
    (
      tourId: string,
      result: {
        currentStepIndex: number;
        selectedPath?: TourPath | null;
        completedSteps: string[];
      },
      eventName: string
    ) => {
      const definition = getTourById(tourId);
      const totalSteps = definition?.steps.web?.length ?? 0;
      const fullStepIndex = Math.min(result.currentStepIndex, Math.max(0, totalSteps - 1));
      const resolvedPath = resolveTourPath(
        definition,
        result.selectedPath ?? null,
        result.completedSteps
      );
      const driverStepIndex = mapFullIndexToFilteredIndex(definition, fullStepIndex, resolvedPath);

      posthog?.capture(eventName, {
        tour_id: tourId,
        device_type: DEVICE_TYPE,
        total_steps: totalSteps,
        step_index: fullStepIndex,
        driver_step_index: driverStepIndex,
        selected_path: resolvedPath,
      });

      setTourToPrompt(null);
      setCurrentTourId(tourId);
      setIsActive(true);
      setCurrentTourPath(resolvedPath);

      const targetRoute = resolveStartRoute({
        tourId,
        fullStepIndex,
        driverStepIndex,
        resolvedPath,
        firstHouseholdId,
        currentPathname: location.pathname,
      });

      // Navigate if we're not already on the target route
      if (location.pathname !== targetRoute) {
        pendingNavigationRef.current = { tourId, stepIndex: driverStepIndex };
        pendingPathRef.current = resolvedPath;
        navigate({ to: targetRoute });
        return;
      }

      // Initialize and start driver from saved position and path
      const driverInstance = initializeDriver(tourId, driverStepIndex, resolvedPath);
      if (driverInstance) {
        driverRef.current = driverInstance;
        void driveTourStepWhenReady(
          driverInstance,
          () => driverRef.current === driverInstance,
          tourId,
          driverStepIndex,
          resolvedPath
        );
      }
    },
    [posthog, setTourToPrompt, firstHouseholdId, location.pathname, navigate, initializeDriver]
  );

  // Start a tour
  const startTour = useCallback(
    async (tourId: string) => {
      try {
        cleanupDriver();

        const result = await startTourMutation.mutateAsync({
          tourId,
          deviceType: DEVICE_TYPE,
        });

        // If tour is already completed, just dismiss the prompt
        if (result.status === 'completed' || result.status === 'skipped') {
          setTourToPrompt(null);
          posthog?.capture('tour_already_completed', {
            tour_id: tourId,
            device_type: DEVICE_TYPE,
            status: result.status,
          });
          return;
        }

        activateAndDriveTour(tourId, result, 'tour_started');
      } catch (error) {
        console.error('Failed to start tour:', error);
        setTourToPrompt(null);
      }
    },
    [startTourMutation, cleanupDriver, activateAndDriveTour, setTourToPrompt, posthog]
  );

  // Store startTour in ref for handoff support
  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  // Resume a tour
  const resumeTour = useCallback(
    async (tourId: string) => {
      try {
        cleanupDriver();

        const result = await resumeTourMutation.mutateAsync({
          tourId,
          deviceType: DEVICE_TYPE,
        });

        activateAndDriveTour(tourId, result, 'tour_resumed');
      } catch (error) {
        console.error('Failed to resume tour:', error);
      }
    },
    [resumeTourMutation, cleanupDriver, activateAndDriveTour]
  );

  // Complete current step
  const completeCurrentStep = useCallback(async () => {
    if (!currentTourId || !driverRef.current) return;

    const definition = getTourById(currentTourId);
    const filteredSteps = getPathFilteredSteps(definition, currentTourPath);
    const stepId = filteredSteps[currentStepIndex]?.id;

    if (stepId) {
      try {
        await completeStepMutation.mutateAsync({
          tourId: currentTourId,
          deviceType: DEVICE_TYPE,
          stepId,
          selectedPath: currentTourPath ?? undefined,
        });
        driverRef.current.moveNext();
      } catch (error) {
        console.error('Failed to complete step:', error);
      }
    }
  }, [currentTourId, currentStepIndex, currentTourPath, completeStepMutation]);

  // Complete the tour
  const completeTour = useCallback(async () => {
    if (!currentTourId) return;
    driverRef.current?.destroy();
    const definition = getTourById(currentTourId);
    const lastStep = definition?.steps.web?.[definition.steps.web.length - 1] as
      | TourStep
      | undefined;
    await handleTourCompleteRef.current?.(currentTourId, lastStep?.handoffTo);
  }, [currentTourId]);

  // Pause tour (for interactive steps where user wants to continue later)
  const pauseTour = useCallback(async () => {
    if (!currentTourId) return;
    await handleTourExitRef.current?.(currentTourId);
  }, [currentTourId]);

  // Exit tour
  const exitTour = useCallback(async () => {
    if (!currentTourId) return;
    await handleTourExitRef.current?.(currentTourId);
  }, [currentTourId]);

  // Skip tour
  const skipTour = useCallback(
    async (reason?: SkipReason) => {
      const tourId = currentTourId || tourToPrompt;
      if (!tourId) return;

      try {
        await skipTourMutation.mutateAsync({
          tourId,
          deviceType: DEVICE_TYPE,
          reason,
        });
        posthog?.capture('tour_skipped', {
          tour_id: tourId,
          device_type: DEVICE_TYPE,
          skip_reason: reason,
          step_index: currentStepIndex,
        });
      } catch (error) {
        console.error('Failed to skip tour:', error);
      }

      driverRef.current?.destroy();
      setIsActive(false);
      setCurrentTourId(null);
      setCurrentStepId(null);
      setTourToPrompt(null);
      driverRef.current = null;
    },
    [currentTourId, tourToPrompt, skipTourMutation, currentStepIndex, posthog, setTourToPrompt]
  );

  // Remind later
  const remindLater = useCallback(async () => {
    const tourId = currentTourId || tourToPrompt;
    if (!tourId) return;

    try {
      const result = await remindLaterMutation.mutateAsync({
        tourId,
        deviceType: DEVICE_TYPE,
      });
      posthog?.capture('tour_remind_later', {
        tour_id: tourId,
        device_type: DEVICE_TYPE,
        remind_count: result.remindCount,
      });
    } catch (error) {
      console.error('Failed to set remind later:', error);
    }

    driverRef.current?.destroy();
    setIsActive(false);
    setCurrentTourId(null);
    setCurrentStepId(null);
    setTourToPrompt(null);
    driverRef.current = null;
  }, [currentTourId, tourToPrompt, remindLaterMutation, posthog, setTourToPrompt]);

  // Check if tour should be prompted
  const shouldPrompt = useCallback(
    (tourId: string) => {
      const tour = tours?.find((t) => t.tourId === tourId);
      if (!tour) return false;
      return shouldPromptTour(tour.web);
    },
    [tours]
  );

  // Select tour path (called from path choice UI)
  const selectTourPath = useCallback(
    (path: TourPath) => {
      setCurrentTourPath(path);
      posthog?.capture('tour_path_selected', {
        tour_id: currentTourId,
        path,
        device_type: DEVICE_TYPE,
      });
    },
    [currentTourId, posthog]
  );

  const value: TourContextValue = {
    isActive,
    currentTourId,
    currentStepId,
    tours,
    isLoading,
    startTour,
    completeCurrentStep,
    completeTour,
    skipTour,
    remindLater,
    exitTour,
    resumeTour,
    shouldPrompt,
    dismissPrompt,
    tourToPrompt,
    pauseTour,
    setFirstHouseholdId,
    firstHouseholdId,
    setItemCount,
    currentTourPath,
    selectTourPath,
    isWaitingForHousehold,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
